/**
 * main.ts — the recipient app: a tiny hand-rolled view router driving the
 * candle-lit unlock → reveal flow.
 *
 *   /unlock/:token   → Landing (sender summary) → Verify (OTP) → Reveal → Library
 *   /onboard         → one-time key setup (generate keypair, register public key)
 *
 * The email's unlock link is `${UNLOCK_BASE_URL}/unlock/:token`. The reveal
 * decrypts on-device via the vendored client crypto. Opened messages are kept
 * in an on-device library (this session) so they can be re-read.
 */
import './tokens.css';
import { RecipientApiClient, ApiError, type UnlockInfo, type RecipientInboxItem, type WelcomeLink } from './api.js';
import { decryptMessage, onboardGenerateKeys, hasKeyFor, sealLetterReply } from './crypto.js';

const api = new RecipientApiClient('');
const app = document.getElementById('app')!;

/** localStorage key for the durable "remember-this-device" token. (The onboarded
 *  email is stored under 'la_recipient_email', read where the device key is looked up.) */
const SESSION_KEY = 'la_recipient_session';

/** Current short-lived access token, refreshed from the durable session on return. */
let accessToken: string | null = null;

/** In-session blob cache for instant replay: messageId → object URL + meta. The
 *  durable LIST of messages now comes from the server inbox (listInbox). */
const library = new Map<string, { url: string; format: string; mime: string }>();

function el(html: string): HTMLElement {
  const t = document.createElement('div');
  t.innerHTML = html.trim();
  return t.firstElementChild as HTMLElement;
}
function render(node: HTMLElement) { app.replaceChildren(node); }
function tokenFromUrl(): string | null {
  const m = location.pathname.match(/\/unlock\/([^/]+)/);
  if (m) return decodeURIComponent(m[1]);
  return new URLSearchParams(location.search).get('token');
}

// ── Screen: Landing ────────────────────────────────────────────────────────
async function screenLanding(token: string) {
  render(el(`<section class="la-card la-rise"><p class="la-body la-muted">Opening…</p></section>`));
  let info: UnlockInfo;
  try {
    info = await api.unlockInfo(token);
  } catch (e) {
    return screenError(e, token);
  }
  const sender = info.sender_name?.trim() || 'Someone who loves you';
  const card = el(`
    <section class="la-card la-rise">
      <p class="la-label">Legacy Architect</p>
      <h1 class="la-title">A message has been waiting for you.</h1>
      <p class="la-body">${escapeHtml(sender)} recorded something for a moment they wanted to share with you. To open it, we'll confirm it's really you.</p>
      <div class="la-cta-wrap"><button class="la-btn-primary" id="go">Continue</button></div>
      <p class="la-muted" style="text-align:center;margin-top:18px">This link is private — please don't forward it.</p>
    </section>`);
  card.querySelector('#go')!.addEventListener('click', () => screenVerify(token));
  render(card);
}

// ── Screen: Verify (OTP) ─────────────────────────────────────────────────────
function screenVerify(token: string) {
  const card = el(`
    <section class="la-card la-rise">
      <p class="la-label">Confirm it's you</p>
      <h1 class="la-title">Enter your code</h1>
      <p class="la-body">We sent a 6-digit code to the contact on file. Enter it below to open your message.</p>
      <input class="la-otp" id="otp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="••••••" aria-label="6-digit code" />
      <p class="la-error" id="err"></p>
      <div class="la-cta-wrap"><button class="la-btn-primary" id="go" disabled>Open the message</button></div>
    </section>`);
  const otp = card.querySelector<HTMLInputElement>('#otp')!;
  const go = card.querySelector<HTMLButtonElement>('#go')!;
  const err = card.querySelector('#err')!;
  otp.addEventListener('input', () => {
    otp.value = otp.value.replace(/\D/g, '').slice(0, 6);
    go.disabled = otp.value.length !== 6;
  });
  go.addEventListener('click', async () => {
    go.disabled = true; err.textContent = ''; go.textContent = 'Opening…';
    try {
      const session = await api.session(token, otp.value);
      // Remember this device so the recipient can return to their library with no redo.
      localStorage.setItem(SESSION_KEY, session.durableToken);
      accessToken = session.accessToken;
      await screenReveal(session.messageId, session.accessToken);
    } catch (e) {
      go.textContent = 'Open the message'; go.disabled = false;
      err.textContent = e instanceof ApiError && e.status === 409
        ? 'That code didn’t match, or it has expired. Please check and try again.'
        : friendly(e);
    }
  });
  render(card);
  setTimeout(() => otp.focus(), 50);
}

// ── Screen: Reveal ───────────────────────────────────────────────────────────
async function screenReveal(messageId: string, accessToken: string) {
  render(el(`<section class="la-card la-rise"><p class="la-body la-muted" style="text-align:center">Decrypting your message…</p></section>`));

  // Resolve which recipient email to look up the private key under. For the
  // demo the recipient onboarded under a known email; we read it from storage.
  const email = localStorage.getItem('la_recipient_email') ?? '';
  try {
    const payload = await decryptMessage({ api: api.decryptApi(accessToken), messageId, email });
    const url = URL.createObjectURL(payload.blob);
    library.set(messageId, { url, format: payload.format, mime: payload.mimeType });
    renderMessage(messageId, accessToken);
  } catch (e) {
    renderRevealError(e);
  }
}


/**
 * A delivered message belongs to the person it was left for.
 *
 * The encryption exists to make sure only they can open it — not to keep it
 * locked away afterwards. Once it is theirs, they should be able to keep it,
 * back it up, or share it with the rest of the family if they want to. Video
 * and voice previously rendered a player with no save at all: Chrome sometimes
 * offers a download in its own controls, Safari on iPhone generally does not,
 * so on the most likely device there was no way to keep it.
 */
function saveFileName(format: string, mime: string): string {
  const ext =
    /mp4/.test(mime) ? 'mp4' :
    /webm/.test(mime) ? 'webm' :
    /mpeg|mp3/.test(mime) ? 'mp3' :
    /wav/.test(mime) ? 'wav' :
    /ogg/.test(mime) ? 'ogg' :
    format === 'letter' ? 'txt' : 'bin';
  return `A message for you.${ext}`;
}

function saveLinkHtml(url: string, format: string, mime: string): string {
  return `<div style="margin-top:18px;text-align:center">
      <a class="la-btn-ghost" href="${url}" download="${saveFileName(format, mime)}">Save this message</a>
    </div>`;
}

function renderMessage(messageId: string, accessToken?: string) {
  const item = library.get(messageId)!;
  let bodyHtml: string;
  if (item.format === 'letter') {
    // Letter blob is text/plain; read + display.
    bodyHtml = `<p class="la-letter" id="letterText">Loading…</p>`;
  } else if (item.format === 'video') {
    bodyHtml = `<video class="la-video" controls autoplay playsinline src="${item.url}"></video>`;
  } else if (item.format === 'voice') {
    bodyHtml = `<audio controls autoplay style="width:100%;margin-top:8px" src="${item.url}"></audio>`;
  } else {
    bodyHtml = `<a class="la-btn-ghost" href="${item.url}" download>Download your message</a>`;
  }
  // Every format gets a save, not just the fallback branch.
  if (item.format === 'letter' || item.format === 'video' || item.format === 'voice') {
    bodyHtml += saveLinkHtml(item.url, item.format, item.mime);
  }
  const canReply = accessToken !== undefined;
  const card = el(`
    <section class="la-card la-reveal">
      <p class="la-label">For you</p>
      ${bodyHtml}
      <div style="margin-top:28px;display:flex;gap:12px;align-items:center;justify-content:space-between;flex-wrap:wrap">
        ${canReply ? `<button class="la-btn-ghost" id="reply">Write back</button>` : `<span class="la-muted">Saved to your library</span>`}
        <button class="la-btn-ghost" id="lib">Your library</button>
      </div>
    </section>`);
  card.querySelector('#lib')!.addEventListener('click', () => void screenLibrary());
  if (canReply) {
    card.querySelector('#reply')!.addEventListener('click', () => screenReply(messageId, accessToken!));
  }
  render(card);

  // Playback receipts (E6-S4): the first 'started' tells the owner their message
  // was truly received + watched, not just delivered to an inbox. Best-effort —
  // wired only when we hold the session token (the reveal path), fired once for
  // 'started' and once for 'completed'. A failure never disrupts playback.
  if (accessToken !== undefined && (item.format === 'video' || item.format === 'voice')) {
    const media = card.querySelector<HTMLMediaElement>('video, audio');
    if (media !== null) {
      let startedSent = false;
      media.addEventListener('playing', () => {
        if (startedSent) return;
        startedSent = true;
        void api
          .playbackEvent(accessToken, messageId, 'started', {
            durationSeconds: Number.isFinite(media.duration) ? media.duration : undefined,
          })
          .catch(() => undefined);
      });
      media.addEventListener('ended', () => {
        void api
          .playbackEvent(accessToken, messageId, 'completed', {
            positionSeconds: media.currentTime,
            durationSeconds: Number.isFinite(media.duration) ? media.duration : undefined,
          })
          .catch(() => undefined);
      });
    }
  }

  if (item.format === 'letter') {
    fetch(item.url).then((r) => r.text()).then((t) => {
      const node = card.querySelector('#letterText');
      if (node) node.textContent = t;
    });
  }
}

// ── Screen: Reply (write back to the person who left the message) ────────────
function screenReply(sourceMessageId: string, accessToken: string) {
  const card = el(`
    <section class="la-card la-rise">
      <p class="la-label">Write back</p>
      <p class="la-body">They may never read this — and that is the point. Say it anyway.</p>
      <textarea id="replyText" class="la-input" rows="6"
        style="width:100%;margin-top:12px;resize:vertical;min-height:120px"
        placeholder="Write your reply…" maxlength="5000"></textarea>
      <p class="la-alert" id="replyErr" role="alert" style="display:none"></p>
      <div style="margin-top:20px;display:flex;gap:12px;align-items:center;justify-content:space-between">
        <button class="la-btn-ghost" id="cancel">Back</button>
        <button class="la-btn-primary" id="send">Send it</button>
      </div>
    </section>`);
  const ta = card.querySelector<HTMLTextAreaElement>('#replyText')!;
  const errNode = card.querySelector<HTMLElement>('#replyErr')!;
  const send = card.querySelector<HTMLButtonElement>('#send')!;
  card.querySelector('#cancel')!.addEventListener('click', () => renderMessage(sourceMessageId, accessToken));
  send.addEventListener('click', () => {
    const text = ta.value.trim();
    errNode.style.display = 'none';
    if (text.length === 0) {
      errNode.textContent = 'Write something first.';
      errNode.style.display = 'block';
      return;
    }
    send.disabled = true;
    send.textContent = 'Sending…';
    void (async () => {
      try {
        await sealLetterReply({
          api: {
            fetchResponseContext: (id) => api.responseContext(id, accessToken),
            postResponse: (input) => api.postResponse(input, accessToken),
          },
          sourceMessageId,
          text,
        });
        render(
          el(`
            <section class="la-card la-rise" style="text-align:center">
              <p class="la-label">Sent</p>
              <p class="la-body">Your reply is on its way back to them.</p>
              <button class="la-btn-ghost" id="lib" style="margin-top:20px">Your library</button>
            </section>`),
        );
        document.querySelector('#lib')!.addEventListener('click', () => void screenLibrary());
      } catch (e) {
        send.disabled = false;
        send.textContent = 'Send it';
        errNode.textContent =
          e instanceof ApiError ? e.message : 'We could not send your reply. Please try again.';
        errNode.style.display = 'block';
      }
    })();
  });
  render(card);
}

// ── Screen: Library (durable — backed by the server inbox) ───────────────────
// `retried` guards the 401→refresh→retry path so a persistently-rejected fresh
// token falls through to the return state instead of looping.
async function screenLibrary(retried = false) {
  if (!accessToken) return screenReturn();
  render(el(`<section class="la-card la-rise"><p class="la-body la-muted" style="text-align:center">Opening your library…</p></section>`));
  let items: RecipientInboxItem[];
  try {
    items = (await api.listInbox(accessToken)).messages;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && !retried) return void refreshThen(() => screenLibrary(true));
    if (e instanceof ApiError && e.status === 401) return screenReturn();
    return screenError(e, '');
  }
  if (items.length === 0) {
    return render(el(`<section class="la-card la-rise"><p class="la-label">Your library</p><h1 class="la-title">Your vault awaits its first whisper.</h1><p class="la-body">When a message is delivered to you, it will wait for you here.</p></section>`));
  }
  const rows = items.map((m) => {
    const when = new Date(m.delivered_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const sub = m.title ? `${when} · ${escapeHtml(m.title)}` : when;
    return `<button class="la-btn-ghost" data-id="${escapeHtml(m.id)}" style="display:block;width:100%;text-align:left;margin:10px 0">
        <strong>${escapeHtml(m.sender_name)}</strong><br/><span class="la-muted">${sub}</span>
      </button>`;
  }).join('');
  const card = el(`<section class="la-card la-rise">
      <p class="la-label">Your library</p>
      <h1 class="la-title">Messages kept for you</h1>
      ${rows}
      <button class="la-btn-ghost" id="details" style="margin-top:20px">How we reach you</button>
      <button class="la-btn-ghost" id="signout" style="margin-top:8px;opacity:0.65">Sign out this device</button>
    </section>`);
  card.querySelectorAll<HTMLButtonElement>('[data-id]').forEach((b) =>
    b.addEventListener('click', () => void openFromLibrary(b.dataset.id!)));
  card.querySelector('#details')!.addEventListener('click', () => void screenDetails());
  card.querySelector('#signout')!.addEventListener('click', () => void signOut());
  render(card);
}

/** Your details — the recipient's own "how we reach you" record (E6-S8). Read-only:
 *  it is used only to deliver what was left for them, never for anything else. */
async function screenDetails(retried = false) {
  if (!accessToken) return screenReturn();
  render(el(`<section class="la-card la-rise"><p class="la-body la-muted" style="text-align:center">Loading…</p></section>`));
  let p;
  try {
    p = await api.getProfile(accessToken);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && !retried) return void refreshThen(() => screenDetails(true));
    return screenError(e, '');
  }
  const line = (label: string, value: string | null, verified: boolean) =>
    value === null || value === ''
      ? ''
      : `<div style="display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid var(--color-border-soft)">
           <span>${escapeHtml(label)}<br/><span class="la-muted">${escapeHtml(value)}</span></span>
           <span class="la-muted">${verified ? '✓ confirmed' : 'not confirmed'}</span>
         </div>`;
  const extra = p.contact_methods
    .filter((m) => m.type !== 'email' && m.type !== 'sms')
    .map((m) => line(m.type, m.value, m.verified))
    .join('');
  const card = el(`<section class="la-card la-rise">
      <p class="la-label">How we reach you</p>
      <h1 class="la-title">Your details, ${escapeHtml(p.full_name)}</h1>
      <p class="la-body la-muted">These are the ways your loved one recorded to reach you. We use
        them only to deliver what they left for you — never for anything else.</p>
      ${line('Email', p.primary_email, p.email_verified)}
      ${line('Phone', p.primary_phone, p.phone_verified)}
      ${extra}
      <p class="la-body ${p.verified_method_count >= 2 ? 'la-muted' : ''}" style="margin-top:16px">
        ${p.verified_method_count >= 2
          ? 'You have more than one confirmed way to be reached — that is exactly what keeps a message from getting lost over the years.'
          : 'Only one confirmed way to reach you is on file. If your email or number ever changes, a message could be missed. Ask the person who set this up to add another.'}
      </p>
      <button class="la-btn-ghost" id="back" style="margin-top:20px">Back to your library</button>
    </section>`);
  card.querySelector('#back')!.addEventListener('click', () => void screenLibrary());
  render(card);
}

/** Open a message from the library: replay from the in-session cache if present,
 *  else decrypt on-device (the private key is looked up by the onboarded email). */
async function openFromLibrary(messageId: string, retried = false) {
  if (library.has(messageId)) return renderMessage(messageId);
  if (!accessToken) return screenReturn();
  render(el(`<section class="la-card la-rise"><p class="la-body la-muted" style="text-align:center">Decrypting your message…</p></section>`));
  const email = localStorage.getItem('la_recipient_email') ?? '';
  try {
    const payload = await decryptMessage({ api: api.decryptApi(accessToken), messageId, email });
    library.set(messageId, { url: URL.createObjectURL(payload.blob), format: payload.format, mime: payload.mimeType });
    renderMessage(messageId);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && !retried) return void refreshThen(() => openFromLibrary(messageId, true));
    renderRevealError(e);
  }
}

/** Refresh the short-lived access token from the durable session, then retry `cb`. */
async function refreshThen(cb: () => void | Promise<void>) {
  const durable = localStorage.getItem(SESSION_KEY);
  if (!durable) return screenReturn();
  try {
    const r = await api.refreshSession(durable);
    accessToken = r.accessToken;
    await cb();
  } catch {
    localStorage.removeItem(SESSION_KEY);
    accessToken = null;
    screenReturn();
  }
}

/** Sign this device out — revoke the durable session server-side and clear it. */
async function signOut() {
  const durable = localStorage.getItem(SESSION_KEY);
  if (durable) { try { await api.revokeSession(durable); } catch { /* revoke best-effort */ } }
  localStorage.removeItem(SESSION_KEY);
  accessToken = null;
  screenReturn();
}

// ── Screen: Return (no active link, no/expired session) ──────────────────────
function screenReturn() {
  render(el(`
    <section class="la-card la-rise">
      <p class="la-label">Legacy Architect</p>
      <h1 class="la-title">Open your private link to begin.</h1>
      <p class="la-body">Your messages are kept safe for you. Open the private link from your email to view them — once you have, this device will remember you.</p>
    </section>`));
}

// ── Screen: Onboarding (one-time key setup) ──────────────────────────────────
// Two steps, because the server will not write a device public key against an
// email without proof that the caller controls that inbox (S6c 1c): otherwise
// a stranger who merely knew the address could substitute their own key and
// every message sealed to that recipient would be sealed to them instead.
//
//   1. email → POST /onboard/otp/send      (a 6-digit code goes to the inbox)
//   2. code  → POST /onboard/otp/verify    (returns a single-use onboardTicket)
//              → generate the keypair on-device
//              → POST /onboard/public-key  (Bearer onboardTicket)
//
// The ticket is a bearer credential for exactly one key registration — it is
// held in a closure for the life of the screen and never persisted. A recipient
// setting up a NEW device simply repeats the ceremony: proving email control
// again is the whole point, and it re-registers the new device's key.
function screenOnboard() {
  const existing = localStorage.getItem('la_recipient_email') ?? '';
  stepEmail(existing);

  function stepEmail(prefill: string, notice = '') {
    const card = el(`
      <section class="la-card la-rise">
        <p class="la-label">One-time setup</p>
        <h1 class="la-title">Create your secure key</h1>
        <p class="la-body">This device will hold a private key that only you have — it's what lets you open the messages left for you. It never leaves this device. First, confirm the email your messages are sent to.</p>
        <input class="la-otp" id="email" type="email" inputmode="email" autocomplete="email" style="letter-spacing:normal;font-size:16px;font-family:var(--font-body)" placeholder="your email" aria-label="Your email address" value="${escapeHtml(prefill)}" />
        <p class="la-error" id="err">${escapeHtml(notice)}</p>
        <div class="la-cta-wrap"><button class="la-btn-primary" id="go">Send me a code</button></div>
      </section>`);
    const input = card.querySelector<HTMLInputElement>('#email')!;
    const go = card.querySelector<HTMLButtonElement>('#go')!;
    const err = card.querySelector('#err')!;
    go.addEventListener('click', async () => {
      const email = input.value.trim().toLowerCase();
      if (!email.includes('@')) { err.textContent = 'Enter the email your messages are sent to.'; return; }
      go.disabled = true; go.textContent = 'Sending…'; err.textContent = '';
      try {
        await api.onboardOtpSend(email);
        stepCode(email);
      } catch (e) {
        go.disabled = false; go.textContent = 'Send me a code';
        err.textContent = e instanceof ApiError && e.status === 429
          ? 'We’ve sent a few codes already. Wait a few minutes, then try again.'
          : friendly(e);
      }
    });
    render(card);
    setTimeout(() => input.focus(), 50);
  }

  function stepCode(email: string) {
    const card = el(`
      <section class="la-card la-rise">
        <p class="la-label">One-time setup</p>
        <h1 class="la-title">Enter your code</h1>
        <p class="la-body">We sent a 6-digit code to <strong>${escapeHtml(email)}</strong>. Enter it to finish setting up this device.</p>
        <input class="la-otp" id="otp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="••••••" aria-label="6-digit code" />
        <p class="la-error" id="err"></p>
        <div class="la-cta-wrap"><button class="la-btn-primary" id="go" disabled>Create my key</button></div>
        <div style="margin-top:18px;text-align:center"><button class="la-btn-ghost" id="back">Use a different email</button></div>
        <p class="la-muted" id="out" style="margin-top:18px"></p>
      </section>`);
    const otp = card.querySelector<HTMLInputElement>('#otp')!;
    const go = card.querySelector<HTMLButtonElement>('#go')!;
    const back = card.querySelector<HTMLButtonElement>('#back')!;
    const err = card.querySelector('#err')!;
    const out = card.querySelector('#out')!;
    otp.addEventListener('input', () => {
      otp.value = otp.value.replace(/\D/g, '').slice(0, 6);
      go.disabled = otp.value.length !== 6;
    });
    back.addEventListener('click', () => stepEmail(email));
    go.addEventListener('click', async () => {
      go.disabled = true; back.disabled = true; err.textContent = ''; go.textContent = 'Creating…';
      const fail = (message: string) => {
        go.disabled = false; back.disabled = false; go.textContent = 'Create my key';
        err.textContent = message;
      };
      let onboardTicket: string | undefined;
      try {
        ({ onboardTicket } = await api.onboardOtpVerify(email, otp.value));
      } catch (e) {
        return fail(e instanceof ApiError && e.status === 400
          ? 'That code didn’t match, or it has expired. Please check and try again.'
          : friendly(e));
      }
      if (!onboardTicket) return fail('That code didn’t match. Please check and try again.');

      let publicKeyForServer: string;
      try {
        ({ publicKeyForServer } = await onboardGenerateKeys(email));
      } catch {
        // Key generation is on-device (IndexedDB / WebCrypto). A raw DOMException
        // here (private mode, storage quota) shouldn't reach the recipient.
        return fail('This browser wouldn’t let us set up your secure key. Try a different browser, or turn off private browsing.');
      }

      try {
        await api.registerPublicKey(email, publicKeyForServer, onboardTicket);
        localStorage.setItem('la_recipient_email', email);
        back.remove();
        go.textContent = 'Done';
        out.innerHTML = `<strong>Your key is ready.</strong><br/>This device can now open the messages sealed for <code>${escapeHtml(email)}</code>.`;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          // The ticket or the underlying proof went stale (each code authorizes
          // exactly one registration) — the honest recovery is a fresh code.
          return stepEmail(email, 'That code has already been used or has expired. We’ll send you a new one.');
        }
        fail(friendly(e));
      }
    });
    render(card);
    setTimeout(() => otp.focus(), 50);
  }
}

// ── Screen: Welcome (/recipient-welcome?t=…) ─────────────────────────────────
//
// The invite the vault owner sends lands here — and until now this route was a
// 404 in every app, which is why the core loop did not close: the delivery
// worker will not send to an email contact whose `verified_at` is NULL, and the
// ONLY code that sets it lives behind this screen.
//
// One code does two jobs, and it has to:
//   1. it flips the email contact to verified          → delivery is unblocked
//   2. it authorizes ONE device-key registration       → the message can be read
// The address is resolved server-side from the invite token — the person who
// recorded the message chose it — so the browser never asserts who it is. That
// makes this ceremony strictly stronger than /onboard, where the email is typed.
//
// Then we link the account (recipients.user_id → users.public_key), because that
// linkage is what lets a message be sealed TO this person's key at all.
function screenWelcome(token: string) {
  stepLanding();

  function stepLanding() {
    const card = el(`
      <section class="la-card la-rise">
        <p class="la-label">Legacy Architect</p>
        <h1 class="la-title">Someone has prepared a message for you.</h1>
        <p class="la-body">A person who loves you has recorded something to reach you at a moment they had in mind. Before it can find you, we need to confirm we have the right person — and set up the private key that will open it.</p>
        <p class="la-error" id="err"></p>
        <div class="la-cta-wrap"><button class="la-btn-primary" id="go">Send me a code</button></div>
        <p class="la-muted" style="text-align:center;margin-top:18px">This link is private — please don't forward it.</p>
      </section>`);
    const go = card.querySelector<HTMLButtonElement>('#go')!;
    const err = card.querySelector('#err')!;
    go.addEventListener('click', async () => {
      go.disabled = true; go.textContent = 'Sending…'; err.textContent = '';
      try {
        const start = await api.welcomeStart(token);
        stepCode(start.recipient_first_name);
      } catch (e) {
        go.disabled = false; go.textContent = 'Send me a code';
        err.textContent = welcomeFailure(e);
      }
    });
    render(card);
  }

  function stepCode(firstName: string, notice = '') {
    const card = el(`
      <section class="la-card la-rise">
        <p class="la-label">Confirm it's you</p>
        <h1 class="la-title">${escapeHtml(firstName)}, enter your code.</h1>
        <p class="la-body">We sent a 6-digit code to the address your message will be delivered to. Entering it confirms we can reach you — without it, nothing can be sent.</p>
        <input class="la-otp" id="otp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="••••••" aria-label="6-digit code" />
        <p class="la-error" id="err">${escapeHtml(notice)}</p>
        <div class="la-cta-wrap"><button class="la-btn-primary" id="go" disabled>Confirm</button></div>
      </section>`);
    const otp = card.querySelector<HTMLInputElement>('#otp')!;
    const go = card.querySelector<HTMLButtonElement>('#go')!;
    const err = card.querySelector('#err')!;
    otp.addEventListener('input', () => {
      otp.value = otp.value.replace(/\D/g, '').slice(0, 6);
      go.disabled = otp.value.length !== 6;
    });
    go.addEventListener('click', async () => {
      go.disabled = true; err.textContent = ''; go.textContent = 'Confirming…';
      const fail = (message: string) => {
        go.disabled = false; go.textContent = 'Confirm'; err.textContent = message;
      };
      let linked: WelcomeLink;
      try {
        linked = await api.welcomeLink(token, { code: otp.value });
      } catch (e) {
        return fail(e instanceof ApiError && (e.status === 400 || e.status === 409)
          ? 'That code didn’t match, or it has expired. Please check and try again.'
          : welcomeFailure(e));
      }
      go.textContent = 'Setting up your key…';
      await setUpKey(linked, firstName, fail);
    });
    render(card);
    setTimeout(() => otp.focus(), 50);
  }

  /**
   * The code is proven and the contact is verified — delivery is now possible.
   * What remains is the key that makes the message *readable*, and the account
   * linkage that lets it be sealed to that key in the first place.
   */
  async function setUpKey(linked: WelcomeLink, firstName: string, fail: (m: string) => void) {
    const email = linked.email;

    // The onboard ticket proves this browser just proved inbox control. The
    // server requires it to link the account (linkage rebinds the seal target,
    // so it must not be reachable with only a leaked invite token). It is present
    // on every verified-code result; its absence means something is wrong.
    if (linked.onboard_ticket === null) {
      return fail('We couldn’t finish setting up your key. Please request a new code.');
    }
    const ticket = linked.onboard_ticket;

    // Already has a key registered (the dual-role case — someone who both records
    // and receives). Never rotate it: messages in other vaults are sealed to it.
    // Just bind this recipient row to that existing account.
    if (!linked.needs_key && linked.existing_user_id !== null) {
      if (!(await hasKeyFor(email))) {
        return fail('Your key lives on the device you first set up. Please open this link there — it’s the only device that can read your messages.');
      }
      try {
        await api.welcomeLink(token, { userId: linked.existing_user_id, onboardTicket: ticket });
      } catch (e) {
        return fail(welcomeFailure(e));
      }
      localStorage.setItem('la_recipient_email', email);
      return stepDone(firstName, email);
    }

    let publicKeyForServer: string;
    try {
      ({ publicKeyForServer } = await onboardGenerateKeys(email));
    } catch {
      // On-device WebCrypto/IndexedDB — a raw DOMException (private mode, quota)
      // must never reach the recipient.
      return fail('This browser wouldn’t let us set up your secure key. Try a different browser, or turn off private browsing.');
    }

    let userId: string;
    try {
      ({ userId } = await api.registerPublicKey(email, publicKeyForServer, ticket));
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        return stepCode(firstName, 'That code has already been used or has expired. We’ll send you a new one.');
      }
      return fail(welcomeFailure(e));
    }

    // Link the account. Until recipients.user_id points at a user with a public
    // key, a message CANNOT be wrapped to this person — so this is not optional
    // bookkeeping; it is the last link in the chain. The ticket re-proves inbox
    // control for the rebind.
    try {
      await api.welcomeLink(token, { userId, onboardTicket: ticket });
    } catch (e) {
      return fail(welcomeFailure(e));
    }
    localStorage.setItem('la_recipient_email', email);
    stepDone(firstName, email);
  }

  function stepDone(firstName: string, email: string) {
    render(el(`
      <section class="la-card la-rise">
        <p class="la-label">You're ready</p>
        <h1 class="la-title">We can reach you now, ${escapeHtml(firstName)}.</h1>
        <p class="la-body">Your key is set up on this device, and <code>${escapeHtml(email)}</code> is confirmed. When the moment they chose arrives, your message will be sent here — and this is the device that can open it.</p>
        <p class="la-muted" style="margin-top:18px">Nothing to do until then. We'll email you.</p>
      </section>`));
  }
}

/** Invite-link failures, said plainly — an expired or spent link is common, not exceptional. */
function welcomeFailure(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return 'This link isn’t valid. It may have been mistyped, or replaced by a newer invitation.';
    if (e.status === 410) return 'This invitation has expired. Ask for a new one to be sent.';
    if (e.status === 409) return 'This invitation has already been used. If this device is set up, your messages will reach you.';
    if (e.status === 429) return 'We’ve sent a few codes already. Wait a few minutes, then try again.';
  }
  return friendly(e);
}

// ── Errors ───────────────────────────────────────────────────────────────────
function screenError(e: unknown, _token: string) {
  const msg = e instanceof ApiError && (e.status === 404 || e.status === 410)
    ? 'This link isn’t valid anymore. It may have expired or already been used.'
    : friendly(e);
  render(el(`<section class="la-card la-rise"><p class="la-label">Legacy Architect</p><h1 class="la-title">We couldn’t open this link.</h1><p class="la-body">${escapeHtml(msg)}</p></section>`));
}
function renderRevealError(e: unknown) {
  const hint = (localStorage.getItem('la_recipient_email') ?? '') === ''
    ? 'This device hasn’t been set up with your key yet. Visit the setup link first.'
    : friendly(e);
  render(el(`<section class="la-card la-rise"><p class="la-label">For you</p><h1 class="la-title">We reached your message, but couldn’t unlock it here.</h1><p class="la-body">${escapeHtml(hint)}</p></section>`));
}
function friendly(e: unknown): string { return e instanceof Error ? e.message : 'Something went wrong. Please try again.'; }
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

// ── Boot ─────────────────────────────────────────────────────────────────────
async function boot() {
  if (location.pathname.startsWith('/onboard')) return void screenOnboard();
  // The invite email points here. Checked BEFORE the unlock branch below: this
  // route carries an invite token (`?t=`), not an unlock token, and the two
  // resolve against different tables.
  if (location.pathname.startsWith('/recipient-welcome')) {
    const t = new URLSearchParams(location.search).get('t');
    if (t) return void screenWelcome(t);
    return void screenError(new ApiError(404, 'missing token'), '');
  }
  const token = tokenFromUrl();
  if (token) return void screenLanding(token);
  // No unlock link in the URL — try the durable "remember-this-device" session
  // so a returning recipient lands straight in their library with no redo.
  const durable = localStorage.getItem(SESSION_KEY);
  if (durable) {
    try {
      const r = await api.refreshSession(durable);
      accessToken = r.accessToken;
      return void screenLibrary();
    } catch {
      localStorage.removeItem(SESSION_KEY); // expired/revoked → fall through to the return state
    }
  }
  screenReturn();
}
void boot();
